import {
  Box,
  Flex,
  Image,
  Link,
  Typography as ReeferTypography,
  StarFilledIcon,
} from '@iheartjane/reefer';
import { sentenceCase } from '@iheartjane/utils';
import { clsx } from 'clsx';
import type { ComponentType, MouseEvent } from 'react';
import { useState } from 'react';

import {
  LINEAGE_COLORS,
  REVIEWS_REQUIRED_BEFORE_DISPLAY,
} from '../../../utils/constants';
import type {
  CustomImageAdProduct,
  CustomImageAdViewModel,
} from '../webCustomImageAd.types';

import styles from './adProductCard.module.css';

interface AdProductCardProps {
  onProductClick?: (product: CustomImageAdProduct) => void;
  product: CustomImageAdViewModel;
  typography?: ComponentType;
}

export const AdProductCard = ({
  onProductClick,
  product,
  typography,
}: AdProductCardProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const Typography = typography || ReeferTypography;

  const showRating =
    product.aggregate_rating > 0 &&
    product.review_count >= REVIEWS_REQUIRED_BEFORE_DISPLAY;

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    product.clickHandler();
    onProductClick?.(product);
    event.stopPropagation();
  };

  const lineageColor = product.category
    ? LINEAGE_COLORS[product.category.toLowerCase()] || 'grays-black'
    : 'grays-black';

  return (
    <Link
      className={clsx(styles['card'], {
        [styles['imageLoaded']]: imageLoaded,
        [styles['imageNotLoaded']]: !imageLoaded,
      })}
      to={product.pdpUrl}
      variant="minimal"
      onClick={handleClick}
    >
      <Flex
        className={styles['shell']}
        background="grays-white"
        flexDirection="column"
        borderRadius="xs"
        height="100%"
        overflow="hidden"
      >
        <Box className={styles['imageSlot']} width="100%" height="auto">
          <Image
            altText={product.name}
            src={product.image_urls[0]}
            objectFit="cover"
            responsive
            width="100%"
            height="100%"
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
          />
        </Box>

        <Flex
          className={styles['content']}
          flexDirection="column"
          minHeight={0}
          p={8}
        >
          <Flex
            alignItems="center"
            gap={4}
            width="100%"
            justifyContent="space-between"
          >
            <Typography
              className={styles['lineage']}
              variant="small-body-semibold"
              color={lineageColor}
              maxLines={1}
            >
              {product.category ? sentenceCase(product.category) : ''}
            </Typography>

            {showRating && (
              <Flex alignItems="center" gap={4} className={styles['rating']}>
                <StarFilledIcon size="sm" />
                <Typography
                  variant="small-body-semibold"
                  color="grays-dark"
                  maxLines={1}
                >
                  {product.aggregate_rating}
                  <span className={styles['reviewCount']}>
                    ({product.review_count})
                  </span>
                </Typography>
              </Flex>
            )}
          </Flex>

          <Typography
            variant="small-body-semibold"
            color="grays-black"
            maxLines={2}
            overflowWrap="anywhere"
          >
            {product.name}
          </Typography>
        </Flex>
      </Flex>
    </Link>
  );
};
